import ReactMarkdown from 'react-markdown'
import { Text, Stack, Box, Button, Card, CardHeader, CardHeading, CardBody } from '~/design_system'
import { Log } from '~/settings/settingsApi'
import { renderLogMessage } from '~/activity_log/activity_log'
import { markdownEscapedTags } from '~/components/ActivityLog'
import { Timestamp } from '~/components/Timestamp'
import { MutedSkeleton, MutedSkeletonText } from '~/common/skeletons'
import { Msg } from '~/common/localizationUtils'
import { ItemsList, ItemsListItems, ItemsListItem } from './ItemsList'

type ActivityLogProps = {
  /** The logs to display. */
  logs?: Log[]

  /** Option to enable collapse on logs */
  maxHeight?: number | string
  w?: number | string

  isLoading?: boolean

  showLoadMore?: boolean
  onLoadMore?: () => void
  isFetchingNextPage?: boolean
}

/**
 * `Card` displayed when no activity log entries were found.
 */
const NoResultsCard = ({ isLoading = false }) => (
  <Card data-testid="activity_log">
    <CardHeader>
      <MutedSkeleton isLoaded={!isLoading}>
        <CardHeading>
          <Msg id="term.activity" description="Activity" />
        </CardHeading>
      </MutedSkeleton>
    </CardHeader>
    <CardBody>
      <MutedSkeletonText noOfLines={2} skeletonHeight={4} isLoaded={!isLoading} maxW="600px">
        <Text>
          <Msg id="activity_log.no_activity" description="No activity has been recorded." />
        </Text>
      </MutedSkeletonText>
    </CardBody>
  </Card>
)

/**
 * Displays a list of activity logs in a user-friendly format.
 *
 * @see `ActivityLog.stories.jsx`
 */
export const ActivityLog = ({
  logs = [],
  maxHeight,
  w,
  isLoading = false,
  showLoadMore = false,
  isFetchingNextPage = false,
  onLoadMore
}: ActivityLogProps) => {
  const startingNumOfLogs = 5
  const hasLogs = !!logs.length

  if (!hasLogs || isLoading) {
    return <NoResultsCard isLoading={isLoading} />
  }

  return (
    <Card data-testid="activity_log" w={w}>
      <CardHeader>
        <CardHeading m={0}>
          <Msg id="term.activity" description="Activity" />
        </CardHeading>
      </CardHeader>
      <CardBody p={0}>
        <ItemsList border={0} wordBreak="break-word">
          <ItemsListItems maxHeight={maxHeight ?? 93.2 * startingNumOfLogs}>
            {logs.map(log => {
              return (
                <ItemsListItem key={log.date.toString()}>
                  <ActivityLogRow log={log} />
                </ItemsListItem>
              )
            })}
            {showLoadMore && (
              <ItemsListItem justifyContent="center">
                <Button variant="inlineLink" isLoading={isFetchingNextPage} onClick={() => onLoadMore && onLoadMore()}>
                  <Msg id="activity_log.load_more" description="Load More" />
                </Button>
              </ItemsListItem>
            )}
          </ItemsListItems>
        </ItemsList>
      </CardBody>
    </Card>
  )
}

export function ActivityLogRow({ log }: { log: Log }) {
  return (
    <Box sx={{ strong: { fontWeight: 'semibold' } }}>
      <Stack spacing={2} data-testid="log-item">
        <div data-testid="log_item_message">
          <ReactMarkdown components={{ p: 'div' }}>{markdownEscapedTags(renderLogMessage(log))}</ReactMarkdown>
        </div>
        <UserNote userFullName={log.user_full_name} impersonatorName={log.impersonator_name} />
        {log.bulk_update_id && <BulkNote bulkUpdateId={log.bulk_update_id} />}
        {log.mcp_source ? <McpNote /> : log.api_source && <ApiNote />}
        <div data-testid="log_item_date">
          <Text textStyle="subtextSmall">
            <Timestamp datetime={log.date} />
          </Text>
        </div>
      </Stack>
    </Box>
  )
}

type BulkNoteProps = {
  bulkUpdateId: number
}

const BulkNote = ({ bulkUpdateId }: BulkNoteProps) => (
  <div data-testid="log_item_bulk_note">
    <Text textStyle="subtextSmall">
      <Msg
        id="activity_log.bulk_update.id_reference"
        bulk_update_id={bulkUpdateId}
        description="- Changed by Bulk Update: {bulkUpdateId}"
      />
    </Text>
  </div>
)

const ApiNote = () => (
  <div data-testid="log_item_api_note">
    <Text textStyle="subtextSmall">
      <Msg id="activity_log.api_source.note" />
    </Text>
  </div>
)

const McpNote = () => (
  <div data-testid="log_item_mcp_note">
    <Text textStyle="subtextSmall">
      <Msg id="activity_log.mcp_source.note" />
    </Text>
  </div>
)

type UserNoteProps = {
  userFullName: string | null
  impersonatorName: string | null
}

const UserNote = ({ userFullName, impersonatorName }: UserNoteProps) => {
  if (!userFullName && !impersonatorName) return null

  return (
    <div data-testid="log_item_user">
      {impersonatorName ? (
        <Text textStyle="subtextSmall">
          <Msg
            id="activity_log.user_note.on_behalf_of"
            description="- Action completed by: {impersonator_name} on behalf of {user_full_name}"
            impersonator_name={impersonatorName}
            user_full_name={userFullName}
          />
        </Text>
      ) : (
        <Text textStyle="subtextSmall">
          <Msg
            id="activity_log.user_note.completed_by"
            description=" - Action completed by: {user_full_name}"
            user_full_name={userFullName}
          />
        </Text>
      )}
    </div>
  )
}
